import { useCallback } from 'react';
import { useSearchParams } from 'react-router';

type SetQueryParamOptions = {
  replace?: boolean;
};

type QueryParamValue = string | null | undefined;

type SetQueryParamValue =
  | QueryParamValue
  | ((currentValue: string | null) => QueryParamValue);

export function useQueryParam(key: string) {
  const [searchParams, setSearchParams] = useSearchParams();
  const queryParamValue = searchParams.get(key);

  const setQueryParam = useCallback(
    (valueOrFn: SetQueryParamValue, options: SetQueryParamOptions = {}) => {
      setSearchParams(
        prevParams => {
          const nextParams = new URLSearchParams(prevParams);
          const currentValue = nextParams.get(key);
          const nextValue =
            typeof valueOrFn === 'function'
              ? valueOrFn(currentValue)
              : valueOrFn;

          if (nextValue === currentValue) {
            return prevParams;
          }

          if (nextValue == null) {
            nextParams.delete(key);
          } else {
            nextParams.set(key, nextValue);
          }

          return nextParams;
        },
        {
          replace: options.replace,
        },
      );
    },
    [key, setSearchParams],
  );

  return [queryParamValue, setQueryParam] as const;
}
